<script setup lang="ts">
import { storeToRefs } from "pinia";
import { ref, reactive, computed } from "vue";
import { useNotificationsStore } from "~/stores/UserNotifications";

const colorMode = useColorMode();
const notificationsStore = useNotificationsStore();
const indexStore = useIndexStore();
const { isSidebarOpened } = storeToRefs(indexStore);

const authStore = useAuthStore();
const active = ref<number>(0);
const menuWrapper = ref();

const locale = useNuxtApp().$i18n.locale;

const showUINotifications = ref(false);
const showUIConfig = ref<boolean>(false);
const showUsers = ref<boolean>(false);
const { signedIn } = storeToRefs(authStore);
const show = (
  uiConfig: boolean,
  uiUsers: boolean,
  uiNotifications: boolean,
) => {
  showUIConfig.value = uiConfig;
  showUsers.value = uiUsers;
  showUINotifications.value = uiNotifications;
};

const logoURL = ref({
  big: "/imgs/light-small-logo.png",
  small: "/imgs/light-big-logo.png",
});

watch(colorMode, () => {
  switch (colorMode.preference) {
    case "light":
      logoURL.value = {
        big: "/imgs/light-small-logo.png",
        small: "/imgs/light-big-logo.png",
      };
      break;
    default:
      logoURL.value = {
        big: "/imgs/dark-small-logo.png",
        small: "/imgs/dark-big-logo.png",
      };
  }
});
onMounted(() => {
  switch (colorMode.preference) {
    case "light":
      logoURL.value = {
        big: "/imgs/light-small-logo.png",
        small: "/imgs/light-big-logo.png",
      };
      break;
    default:
      logoURL.value = {
        big: "/imgs/dark-small-logo.png",
        small: "/imgs/dark-big-logo.png",
      };
  }
});
function scrollWrapper() {
  const offset = 100 * active.value;
  menuWrapper.value.style.transform = `translateX(-${offset}%)`;
}

function changeTab(i: number) {
  active.value = i;
  scrollWrapper();
}

let notificationsInterval: ReturnType<typeof setInterval> | null = null;
const newNotification = computed(
  () => notificationsStore.notificationCount,
);

const fetchNotifications = () => {
  notificationsStore.fetchNotifications();
};

const playNotificationSound = () => {
  const audio = new Audio("/sounds/notification.mp3");
  audio.play();
};

watch(newNotification, (newVal, oldVal = 0) => {
  if (newVal > oldVal) {
    playNotificationSound();
  }
});

watch(signedIn, (isSignedIn) => {
  if (isSignedIn) {
    fetchNotifications();
    notificationsInterval = setInterval(fetchNotifications, 360000);
  } else if (notificationsInterval) {
    clearInterval(notificationsInterval);
    notificationsInterval = null;
  }
});

function toggleSidebar() {
  indexStore.toggleSidebar();
}
onMounted(() => {
  if (signedIn.value) {
    fetchNotifications();
    notificationsInterval = setInterval(fetchNotifications, 360000);
  }
});

onUnmounted(() => {
  if (notificationsInterval) {
    clearInterval(notificationsInterval);
  }
});

function handleStartTour() {
  // On mobile the drawer must close for the tour; on desktop the rail stays as
  // is so the user's persisted collapse preference is not overwritten.
  if (isSidebarOpened.value && !window.matchMedia("(min-width: 993px)").matches) {
    indexStore.toggleSidebar();
  }
  const { startTour } = useDashboardOnboarding();
  nextTick(() => startTour());
}
</script>

<template>
  <div id="sidebar" class="sidebar" :class="{ open: isSidebarOpened }">
    <a class="sidebar__logo" href="/">
      <img class="big" :src="logoURL.big" :alt="$t('brands.community')" />
      <img class="small" :src="logoURL.small" :alt="$t('brands.community')" />
    </a>

    <button aria-controls="sidebar" :aria-label="$t('sidebar.toggler_label')" class="sidebar__toggler"
      @click="toggleSidebar()">
      <MGIcon icon="double-chevron-left" />
    </button>
    <div v-if="isSidebarOpened" class="user-bar">
      <!-- <button v-if="signedIn === true" disabled type="button">
        <MGIcon icon="message" size="2x" />
      </button> -->
      <button v-if="signedIn === true" type="button">
        <a href="#" :class="{ active: showUINotifications }"
          @click.prevent.stop="show(false, false, !showUINotifications)">
          <div v-if="newNotification !== 0" class="unread-circle mx-auto">
            {{ newNotification }}
          </div>
          <MGIcon icon="notification" size="2x" />
        </a>
      </button>
      <button :class="[{ active: showUIConfig }]" type="button" @click="show(!showUIConfig, false)">
        <MGIcon icon="gear" size="2x" />
      </button>
      <transition name="fade">
        <HeaderUIConfig v-if="showUIConfig" @toggle-visible="showUIConfig = !showUIConfig" />
      </transition>

      <button type="button" :style="showUsers ? 'color: black;' : ''" @click="show(false, !showUsers)">
        <MgHeaderAvatarToggle />
      </button>
      <transition name="fade">
        <HeaderNotificationsModal v-if="showUINotifications" @toggle-visible="show(false, false, false)" />
      </transition>
      <transition name="fade">
        <MgHeaderUIUser v-if="showUsers" ref="childDiv" class="header_ui_users"
          @toggle-visible="showUsers = !showUsers" />
      </transition>
    </div>
    <div ref="sidebarContainer" class="sidebar-menu">
      <div ref="menuWrapper" class="sidebar-menu__wrapper">
        <div ref="wrapper__tab" class="sidebar-menu__wrapper__tab">
          <ul class="menus">
            <li class="menus__item">
              <NuxtLink class="menus__item__option" :to="$localePath(`/account/dashboard`)">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" class="icon dashboard-icon">
                  <path d="M12.4 5V11.2H18.6V5H12.4ZM17.4 10H13.6V6.2H17.4V10Z" fill="currentColor" />
                  <path d="M5 11.2H11.2V5H5V11.2ZM6.2 6.2H10V10H6.2V6.2Z" fill="currentColor" />
                  <path d="M12.4 18.6H18.6V12.4H12.4V18.6ZM13.6 13.6H17.4V17.4H13.6V13.6Z" fill="currentColor" />
                  <path d="M5 18.6H11.2V12.4H5V18.6ZM6.2 13.6H10V17.4H6.2V13.6Z" fill="currentColor" />
                </svg>
                <span>{{ $t("sidebar.menus.dashboard") }}</span>
              </NuxtLink>
            </li>
            <li class="menus__item">
              <NuxtLink class="menus__item__option" :to="$localePath(`/account/personal-informations`)">
                <MGIcon icon="profile" />
                <span>{{ $t("sidebar.menus.infos") }}</span>
              </NuxtLink>
            </li>
            <li class="menus__item">
              <NuxtLink class="menus__item__option" :to="$localePath(`/account/my-companies`)">
                <MGIcon icon="business-operations" />
                <span>{{ $t("sidebar.menus.my_companies") }}</span>
              </NuxtLink>
            </li>
            <li class="menus__item">
              <NuxtLink class="menus__item__option" :to="$localePath(`/account/my-games`)">
                <MGIcon icon="games" />
                <span>{{ $t("sidebar.menus.my_games") }}</span>
              </NuxtLink>
            </li>
            <li class="menus__item">
              <NuxtLink class="menus__item__option" :to="$localePath(`/account/billing`)">
                <MGIcon icon="money" />
                <span>{{ $t("sidebar.menus.billing") }}</span>
              </NuxtLink>
            </li>
            <li class="menus__item">
              <NuxtLink class="menus__item__option" :to="$localePath(`/account/rewards`)">
                <MGIcon icon="coin-v1" />
                <span>{{ $t("sidebar.menus.rewards", "Rewards") }}</span>
              </NuxtLink>
            </li>
            <li class="menus__item">
              <NuxtLink class="menus__item__option" :to="$localePath(`/account/missions`)">
                <MGIcon icon="double-MGC" />
                <span>{{ $t("sidebar.menus.missions") }}</span>
              </NuxtLink>
            </li>
            <li class="menus__item">
              <NuxtLink class="menus__item__option" :to="$localePath(`/account/earn`)">
                <MGIcon icon="partner" />
                <span>{{ $t("sidebar.menus.earn") }}</span>
              </NuxtLink>
            </li>
            <li class="menus__item">
              <NuxtLink class="menus__item__option" :to="$localePath(`/account/support`)">
                <MGIcon icon="message" />
                <span>{{ $t("sidebar.menus.support") }}</span>
              </NuxtLink>
            </li>
            <li class="menus__item">
              <NuxtLink class="menus__item__option" :to="$localePath(`/account/preferences`)">
                <MGIcon icon="gear" />
                <span>{{ $t("sidebar.menus.config") }}</span>
              </NuxtLink>
            </li>
            <li class="menus__item" data-tour="restart-tour">
              <a class="menus__item__option" href="#" @click.prevent="handleStartTour">
                <MGIcon icon="question" />
                <span>{{ $t("dashboard.onboarding.restart_tour_btn") }}</span>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
  <button v-if="isSidebarOpened" class="back-button" type="button" @click="
    () => {
      isSidebarOpened = false;
    }
  "></button>
</template>

<style scoped lang="scss">
.header_ui_users {
  top: 60px;
}

.user-bar {
  display: none;
  background: var(--search-bar-bg);
  width: 100%;
  position: relative;
  padding: 16px;
  column-gap: 16px;

  button {
    background: none;
    border: none;
    outline: none;

    &:not(:disabled) {
      i {
        color: var(--header-fg);
      }
    }
  }
}

#sidebar {
  z-index: 4000;
  display: flex;
  flex-direction: column;
  height: 100vh;
}

.sidebar-menu {
  overflow: hidden;
  height: 100%;
  width: 100%;

  &__wrapper {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    transition: transform 0.4s cubic-bezier(0.86, 0, 0.07, 1);

    &__tab {
      flex: 1 0 100%;
      width: 100%;
      height: 100%;

      &--overflow {
        overflow-y: scroll;
        height: 100%;

        &::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }

        &::-webkit-scrollbar-button {
          width: 0px;
          height: 0px;
        }

        &::-webkit-scrollbar-thumb {
          background: #808080;
          border: 15px none #ffffff;
          border-radius: 0px;
        }

        &::-webkit-scrollbar-thumb:hover {
          background: var(--bt-active);
        }

        &::-webkit-scrollbar-thumb:active {
          background: #aaaaaa;
        }

        &::-webkit-scrollbar-track {
          background: var(--body-bg);
          border: 8px none #ffffff;
          border-radius: 0px;
        }

        &::-webkit-scrollbar-track:hover {
          background: var(--body-bg);
        }

        &::-webkit-scrollbar-track:active {
          background: var(--body-bg-2);
        }

        &::-webkit-scrollbar-corner {
          background: transparent;
        }
      }
    }
  }
}

.sidebar {
  display: flex;
  z-index: 10;
  flex-direction: column;
  /* align-items: center; */
  background: var(--sidebar-bg);
  transition:
    max-width linear 0.3s,
    background ease 0.3s;

  &__logo {
    text-decoration: none;

    .small {
      display: none;
    }

    .big {
      display: none;
    }
  }

  &-menu__wrapper {
    height: 100%;
  }

  .menu-wrapper {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    /* will-change: transform; */
    transition: transform 0.4s cubic-bezier(0.86, 0, 0.07, 1);

    .menu-wrapper__content {
      flex: 1 0 100%;
      width: 100%;
      background-color: var(--header-ui-config-bg);
    }
  }

  &.open {
    width: 100%;
    max-width: 256px;

    .sidebar__logo {
      padding: 1rem 0.5rem;

      .small {
        display: block;
      }
    }

    .sidebar__toggler {
      margin-left: auto;
      margin-right: 0.75rem;

      [dir="rtl"] & {
        margin-left: 0.75rem;
        margin-right: auto;
        transform: rotate(180deg);
      }
    }

    .menus {
      &__item {
        &__option {
          height: 48px;
          justify-content: flex-start;
          padding-left: 1rem;
          font-size: 0.875rem;
          flex-wrap: wrap;
          text-wrap: nowrap;

          span {
            padding-left: 0.5rem;
          }

          i,
          span {
            transition: all ease 0.2s;
          }

          .icon.chevron-right {
            margin-left: auto;
            margin-right: 1rem;
            font-size: 1rem;
          }
        }
      }
    }
  }

  &:not(.open) {
    width: 100%;
    max-width: 70px;

    [dir="rtl"] & {
      max-width: 100px;
    }

    .sidebar__logo {
      padding: 1rem 0.5rem;

      .big {
        display: block;
      }
    }

    .sidebar__toggler {
      transform: rotate(180deg);

      [dir="rtl"] & {
        transform: rotate(0deg);
      }
    }

    .menus {
      &__item {
        justify-content: flex-start;

        &__option {
          flex-wrap: wrap;
          justify-content: center;

          flex-direction: column;

          i,
          span {
            text-align: center;
          }

          span {
            font-size: 0.56rem;
            line-height: 1.2;
            max-width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .icon.chevron-right {
            display: none;
          }
        }
      }
    }
  }

  &__toggler {
    color: var(--sidebar-toggler-fg);
    background: transparent;
    border: 0;
    outline: 0;
    transition: color 0.5s ease-out;

    &:hover {
      color: var(--sidebar-toggler-active-fg);
    }
  }

  .menus {
    width: 100%;
    margin: 0;
    padding-top: 0.75rem;
    padding-left: 0;
    list-style: none;

    &__item {
      .router-link-exact-active {
        background: var(--sidebar-menus-active-bg);
        color: #fff;

        .icon {
          color: var(--sidebar-menus-icon-active-fg);
        }
      }

      &__option {
        cursor: pointer;
        display: flex;
        align-items: center;
        height: 54px;
        width: 100%;
        font-size: 0.625rem;
        color: var(--sidebar-menus-fg);
        text-decoration: none;
        transition:
          color 0.15s ease-in-out,
          background-color 0.15s ease-in-out;

        &.active {
          color: #fff;

          .icon {
            color: var(--bt-active);
          }
        }

        &:hover,
        .router-link-exact-active {
          background: var(--sidebar-menus-active-bg);
          color: #fff;

          .icon {
            color: var(--sidebar-menus-icon-active-fg);
          }
        }

        .icon {
          font-size: 1.525rem;
          color: var(--sidebar-menus-icon-fg);

          &:hover {
            color: var(--sidebar-menus-icon-active-fg);
          }
        }
      }
    }
  }
}

.back-button {
  position: fixed;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 35;
  border: none;
  display: none;
}

@media screen and (max-width: 576px) {
  .back-button {
    display: block;
  }

  .user-bar {
    display: flex;
  }

  .sidebar {
    position: absolute;
    left: -100vw;
    transition: all ease 0.2s;
    width: 50vw;
    height: 100vh;
    top: 0;
    z-index: 10;

    &>* {
      transition: all ease 1s;
    }

    .sidebar-menu {
      width: 100%;
      height: 100%;
    }

    .sidebar-menu__wrapper {
      height: 100%;
    }

    .sidebar-menu__wrapper,
    .sidebar-menu__wrapper__tab {
      width: 100%;
      height: 100%;
    }

    .sidebar__logo,
    .sidebar__toggler {
      display: none;
    }

    &.open {
      left: 0;

      .sidebar-toggler {
        display: none;
      }

      .menus {
        padding-top: 0;
      }
    }
  }
}

@media screen and (min-width: 577px) and (max-width: 992px) {
  .sidebar.open {
    position: absolute;
    left: 0;
    z-index: 10;
  }
}
</style>
